import { Controller } from '@hotwired/stimulus'
import { gantt } from 'dhtmlx-gantt'
import { Modal } from 'bootstrap'
import i18next from 'i18next'

import './gantt_utils/export.dhtmlx.api'
import { getGanttData } from '../../services/sites/calendarGanttService'
import { updateSiteTask } from '../../services/sites/calendarTaskService'
import { createSiteTaskGroup, updateSiteTaskGroup, deleteSiteTaskGroup } from '../../services/sites/siteTaskGroupService'
import { addNotification } from '../../helpers/notificationHelper'
import { attachGanttScrollHandler } from './gantt_utils/ganttScrollUtils'
import { collapseAll as collapseAllUtils, expandToCategories as expandToCategoriesUtils, expandAll as expandAllUtils, setExpandCollapseActiveButton } from './gantt_utils/ganttExpandCollapseUtils'
import {
  hasUnsetDates,
  formatDateForInput,
  getDefaultDatesForSiteTaskGroup,
  wrapSiteTaskGroupDatesAroundTask,
  getEarliestDateInCategory,
  isSiteTaskGroup,
  shouldDisableTaskDrag,
} from './gantt_utils/ganttDateUtils'

// Translation namespace shorthand
const ns = 'controllers__sites__dhtmlx_gantt_show_controller'
const t = (key, options = {}) => i18next.t(key, { ns, ...options })

const darkenColor = (color, quantity = 30) => {
  const hex = color.replace('#', '')
  const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - quantity)
  const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - quantity)
  const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - quantity)
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

const weekFormat = (date) => {
  const dateToStr = gantt.date.date_to_str('%d %M')
  const endDate = gantt.date.add(gantt.date.week_start(date), 6, 'day')
  const startDate = gantt.date.week_start(date)
  return `${dateToStr(startDate)} - ${dateToStr(endDate)}`
}

const GRID_WIDTH_STORAGE_KEY = 'ganttGridWidth'
const DEFAULT_GRID_WIDTH = 360
const MIN_GRID_WIDTH = 220
const MAX_GRID_WIDTH = 900

const readStoredGridWidth = () => {
  try {
    const width = parseInt(localStorage.getItem(GRID_WIDTH_STORAGE_KEY), 10)
    return Number.isFinite(width) ? Math.min(Math.max(width, MIN_GRID_WIDTH), MAX_GRID_WIDTH) : DEFAULT_GRID_WIDTH
  } catch {
    return DEFAULT_GRID_WIDTH
  }
}

const storeGridWidth = (width) => {
  try {
    localStorage.setItem(GRID_WIDTH_STORAGE_KEY, String(Math.round(width)))
  } catch {
    // storage unavailable
  }
}

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]
))

const isScheduledShorterThanEstimate = (task) => task.estimated_days > 0 && task.duration < task.estimated_days

const extractId = (ganttId) => ganttId?.split('-').slice(1).join('-')

// Connects to data-controller="sites--dhtmlx-gantt-show"
export default class extends Controller {
  static targets = ['viewButton', 'hourScaleCheckbox', 'siteTaskGroupModal', 'siteTaskGroupName', 'manualDatesSection', 'siteTaskGroupStartDate', 'siteTaskGroupEndDate', 'legend', 'weekendModeSelect']
  static values = {
    organizationId: String,
    projectId: String,
    siteId: String,
    locale: String,
    periods: { type: Object, default: {} },
    jsI18n: { type: Object, default: {} },
    configuration: { type: Object, default: {} },
    hourDayScale: String,
  }

  // Type colors for visual distinction
  typeColors = {
    'Project': '#80caffff',        // Light blue
    'Category': '#fad0a7ff',       // Light orange
    'SiteTaskGroup': '#ffffffff',  // White
    'SiteTaskGroupBg': '#bebebeff',       // Mid-light gray
    'SiteTask': '#f9ff9cff'        // Light yellow
  }

  connect() {
    this.preventSwipeNavigation = (event) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        event.preventDefault()
        event.stopPropagation()
      }
    }
    this.element.addEventListener('wheel', this.preventSwipeNavigation, { passive: false })

    // Initialize current view and scale
    this.currentView = this.configurationValue?.view || 'week'
    this.currentScale = 'day' // Default scale is always 'day'
    this.isHourScale = false
    this.weekendMode = 'highlight' // 'show' | 'highlight' | 'skip'
    this._skipModePendingWorkDays = null

    this.setupGantt()
    this.loadData()
    this.setupUpdateCallbacks()
  }

  disconnect() {
    if (this.preventSwipeNavigation) {
      this.element.removeEventListener('wheel', this.preventSwipeNavigation)
    }
  }

  get isReadonly() {
    return typeof this.configurationValue?.readonly !== 'undefined' ? this.configurationValue.readonly : false
  }

  rowActionsHtml(task) {
    let buttons = ''
    if (['Project', 'Category'].includes(task.type)) {
      buttons += `
        <button type="button" class="btn btn-sm py-0 px-1 text-secondary"
          data-action="sites--dhtmlx-gantt-show#addSiteTaskGroup"
          data-type="${task.type}" data-id="${task.id}">
          <i class="fas fa-folder-plus"></i>
        </button>
      `
    }
    if (task.type === 'SiteTaskGroup' || task.originalType === 'SiteTaskGroup') {
      buttons += `
        <button type="button" class="btn btn-sm py-0 px-1 text-secondary"
          data-action="sites--dhtmlx-gantt-show#openSiteTaskGroupSettingsDialog"
          data-id="${task.id}">
          <i class="fas fa-cog"></i>
        </button>
      `
    }
    return buttons ? `<div class="cmp-gantt-row-actions">${buttons}</div>` : ''
  }

  setupResizableGrid() {
    gantt.config.grid_width = readStoredGridWidth()
    gantt.config.layout = {
      css: 'gantt_container',
      cols: [
        {
          width: gantt.config.grid_width,
          min_width: MIN_GRID_WIDTH,
          max_width: MAX_GRID_WIDTH,
          rows: [{ view: 'grid', scrollY: 'scrollVer' }],
        },
        { resizer: true, width: 6 },
        {
          rows: [
            { view: 'timeline', scrollX: 'scrollHor', scrollY: 'scrollVer' },
            { view: 'scrollbar', id: 'scrollHor' },
          ],
        },
        { view: 'scrollbar', id: 'scrollVer' },
      ],
    }

    if (this.gridResizeEventId) gantt.detachEvent(this.gridResizeEventId)
    this.gridResizeEventId = gantt.attachEvent('onGridResizeEnd', (_oldWidth, newWidth) => {
      storeGridWidth(newWidth)
      return true
    })
  }

  async setupGantt() {
    gantt.i18n.setLocale(this.localeValue)
    // work_time = false: preserves calendar-day duration during drag.
    // work_time = true caused tasks to shrink/expand when dragged across weekends
    // because DHTMLX preserved work-day count (skipping Sat/Sun), not calendar days.
    gantt.config.work_time = false
    gantt.config.xml_date = '%Y-%m-%d %H:%i'
    gantt.config.date_format = '%Y-%m-%d %H:%i'
    gantt.config.row_height = 40
    gantt.config.show_links = false
    gantt.config.readonly = this.isReadonly

    // Enable drag and drop
    gantt.config.drag_move = !this.isReadonly
    gantt.config.drag_progress = !this.isReadonly
    gantt.config.order_branch = true
    gantt.config.order_branch_free = true

    gantt.templates.grid_row_class = (start, end, task) => {
      const baseClass = task.parent === '0' || !task.parent ? 'cmp-parent-task' : ''
      const typeClass = `gantt-type-${task.originalType || task.type || 'unknown'}`

      // During drag, highlight valid drop targets in green
      let dropTargetClass = ''
      if (this._draggedTaskId && task.id !== this._draggedTaskId) {
        const draggedTask = gantt.getTask(this._draggedTaskId)
        if (this._isValidMove && this._isValidMove(draggedTask, task)) {
          dropTargetClass = 'cmp-valid-drop-target'
        }
      }

      return `${baseClass} ${typeClass} ${dropTargetClass}`.trim()
    }

    gantt.templates.task_text = () => ''

    gantt.templates.rightside_text = (start, end, task) => {
      const parts = [i18next.t('views.gantt.bar_days', { days: task.duration })]
      if (task.estimated_days > 0) parts.push(i18next.t('views.gantt.bar_estimate', { days: task.estimated_days }))
      const warning = isScheduledShorterThanEstimate(task) ? ' <i class="fas fa-triangle-exclamation"></i>' : ''
      const warningClass = warning ? ' cmp-gantt-bar-label-warning' : ''
      return `<span class="cmp-gantt-bar-label${warningClass}">${parts.join(' · ')}${warning}</span>`
    }

    gantt.templates.tooltip_text = (start, end, task) => {
      const lines = [`<b>${escapeHtml(task.text)}</b>`]
      if (task.estimated_days > 0) lines.push(i18next.t('views.gantt.tooltip_estimated', { days: task.estimated_days }))
      lines.push(i18next.t('views.gantt.tooltip_scheduled', { days: task.duration }))
      return lines.join('<br>')
    }

    gantt.templates.task_class = (start, end, task) => {
      const cellClass = task.cellClass || ''
      const typeClass = `gantt-bar-type-${task.originalType || task.type || 'unknown'}`
      // Tasks inside SiteTaskGroups cannot be dragged (see _dhtmlxgantt.scss)
      const dragDisabledClass = task.type === 'SiteTask' && shouldDisableTaskDrag(task) ? 'cmp-task-drag-disabled' : ''
      return `${cellClass} ${typeClass} ${dragDisabledClass}`.trim()
    }

    gantt.templates.scale_cell_class = (date) => {
      if (this.weekendMode === 'show') return ''
      const day = date.getDay()
      return (day === 0 || day === 6) ? 'cmp-weekend' : ''
    }

    gantt.templates.timeline_cell_class = (item, date) => {
      if (this.weekendMode === 'show') return ''
      const day = date.getDay()
      return (day === 0 || day === 6) ? 'cmp-weekend' : ''
    }

    gantt.config.columns = [
      {
        name: 'text',
        label: this.jsI18nValue.task_name,
        tree: true,
        width: '*',
        resize: true,
        editable: !this.isReadonly,
        template: (task) => {
          const titleAttr = task.type === 'Project' && task.siteRevisionTitle
            ? `${task.text} - ${task.siteRevisionTitle}`
            : task.text
          return `<div class="cmp-gantt-task-name" title="${titleAttr}">${task.text}</div>${this.rowActionsHtml(task)}`
        }
      },
    ]

    this.setupResizableGrid()

    gantt.plugins({
      drag_timeline: !this.isReadonly,
      tooltip: true,
    })

    // Setup drag and drop constraints BEFORE init
    this.setupDragDropConstraints()

    gantt.attachEvent('onLightbox', function (_taskId) {
      const userSection = gantt.getLightboxSection('description')
      if (userSection && userSection.node) {
        const userSelectors = userSection.node.querySelectorAll("textarea")
        userSelectors.forEach(el => {
          el.setAttribute('disabled', 'disabled')
        })
      }
    })

    gantt.init('gantt_obj')
    this.addTodayMarker()
    this.addTypeStyles()
    this.renderLegend()

    // Set initial view and scale
    this.updateGanttScales()
    this._setActiveViewButton(this.currentView)
    attachGanttScrollHandler()
  }

  setupDragDropConstraints() {
    // Check if a move would be valid (used by both visual feedback and drop validation)
    const isValidMove = (task, newParent) => {
      // Projects and Categories cannot be moved
      if (['Project', 'Category'].includes(task.type)) {
        return false
      }

      // Get the root project for the task being moved
      const taskProject = this.getRootProject(task)
      const targetProject = newParent ? this.getRootProject(newParent) : null

      // Cannot move items between different projects
      if (taskProject && targetProject && taskProject.id !== targetProject.id) {
        return false
      }

      // If trying to move to root level (no parent), only Projects are allowed there
      if (!newParent && task.type !== 'Project') {
        return false
      }

      // Prevent tasks from being dropped onto other tasks
      if (newParent && newParent.type === 'SiteTask') {
        return false
      }

      // SiteTaskGroups can only contain SiteTasks
      if (newParent && newParent.originalType === 'SiteTaskGroup' && task.type !== 'SiteTask') {
        return false
      }

      // CATEGORY-BASED RULES FOR SITE TASKS
      if (task.type === 'SiteTask') {
        const taskCategory = this.getCategoryForTask(task)
        const targetCategory = this.getCategoryForParent(newParent)

        // Moving to a Category: must be the same category
        if (newParent && newParent.type === 'Category') {
          if (taskCategory !== newParent.id) {
            return false
          }
        }

        // Moving to a SiteTaskGroup: must be in same category OR group has no category
        if (newParent && newParent.originalType === 'SiteTaskGroup') {
          const groupHasNoCategory = !newParent.taskCategoryId
          if (!groupHasNoCategory && targetCategory !== taskCategory) {
            return false
          }
        }
      }

      // CATEGORY-BASED RULES FOR SITE TASK GROUPS
      if (task.originalType === 'SiteTaskGroup') {
        // Non-empty groups cannot be moved to a different category
        const children = gantt.getChildren(task.id)
        if (children.length > 0) {
          const groupCurrentCategory = this.getCategoryForParent(task)
          const targetCategory = this.getCategoryForParent(newParent)

          if (groupCurrentCategory && targetCategory && groupCurrentCategory !== targetCategory) {
            return false
          }

          // Moving from root to category: all children must belong to target category
          const groupIsCurrentlyAtRoot = this.isGroupAtRoot(task)
          if (groupIsCurrentlyAtRoot && newParent && newParent.type === 'Category') {
            const allChildrenInCategory = children.every(childId => {
              const child = gantt.getTask(childId)
              return child.type === 'SiteTask' && this.getCategoryForTask(child) === newParent.id
            })
            if (!allChildrenInCategory) {
              return false
            }
          }
        }
      }

      return true
    }

    // Store isValidMove for use in grid_row_class template
    this._isValidMove = isValidMove
    this._draggedTaskId = null

    // Track drag start/end for visual feedback via grid_row_class
    gantt.attachEvent("onRowDragStart", (id, target, e) => {
      this._draggedTaskId = id
      gantt.render()
      return true
    })

    gantt.attachEvent("onRowDragEnd", (id, target) => {
      this._draggedTaskId = null
      gantt.render()
    })

    // Validate move before it happens
    gantt.attachEvent("onBeforeTaskMove", (id, parent, tindex) => {
      const task = gantt.getTask(id)
      const newParent = parent ? gantt.getTask(parent) : null
      return isValidMove(task, newParent)
    })

    gantt.attachEvent("onAfterTaskMove", (id, parent, tindex) => {
      const task = gantt.getTask(id)
      const newParentGanttId = parent === '0' ? null : parent

      // Only save if parent actually changed
      if (task.$old_parent === newParentGanttId) {
        return
      }

      if (task.type === 'SiteTask') {
        const siteTaskId = extractId(id)
        const newParent = parent ? gantt.getTask(parent) : null
        const newSiteTaskGroupId = (newParent && (newParent.originalType === 'SiteTaskGroup' || newParent.type === 'SiteTaskGroup'))
          ? extractId(parent)
          : null

        // Wrap siteTaskGroup dates around task if siteTaskGroup has unset dates
        if (newParent && isSiteTaskGroup(newParent)) {
          const newDates = wrapSiteTaskGroupDatesAroundTask(newParent, task)
          if (newDates) {
            // Update siteTaskGroup dates in Gantt
            newParent.start_date = newDates.startDate
            newParent.end_date = newDates.endDate
            gantt.updateTask(parent)

            // Save to backend
            const siteTaskGroupId = extractId(parent)
            updateSiteTaskGroup(siteTaskGroupId, {
              start_date: formatDateForInput(newDates.startDate),
              end_date: formatDateForInput(newDates.endDate)
            })
          }
        }

        // Always update, even when null (to remove from group)
        updateSiteTask(siteTaskId, { site_task_group_id: newSiteTaskGroupId }).then(() => {
          addNotification(
            '<span class="fa fa-cloud-arrow-up me-2"></span>' +
            i18next.t('command_result.saved'), 'bg-success', 'gantt-task-saved')
        })
      } else if (task.originalType === 'SiteTaskGroup') {
        const siteTaskGroupId = extractId(id)
        const parentTask = newParentGanttId ? gantt.getTask(newParentGanttId) : null

        // parent_id is only set if parent is another SiteTaskGroup
        // Categories and Projects don't become parents in the backend
        const newParentId = (parentTask && parentTask.originalType === 'SiteTaskGroup')
          ? extractId(newParentGanttId)
          : null

        // task_category_id is set if parent is a Category
        let taskCategoryId = null
        if (parentTask && parentTask.type === 'Category') {
          // Extract category ID from the composite ID (format: "project_id:category_id")
          const categoryIdParts = parentTask.id.split(':')
          taskCategoryId = categoryIdParts.length === 2 ? categoryIdParts[1] : null
        } else if (!newParentId) {
          // If not under a group and not under a category, clear the category
          taskCategoryId = null
        }
        // If moving under another group, don't change task_category_id (keep existing)

        const rootProject = this.getRootProject(task)
        const projectId = rootProject ? rootProject.id : this.projectIdValue

        const updateData = { project_id: projectId, parent_id: newParentId }
        // Only update task_category_id if we explicitly determined it (moving to category or root)
        if (parentTask && parentTask.type === 'Category') {
          updateData.task_category_id = taskCategoryId

          // If group has unset dates and is moved into a category,
          // set dates to earliest date in that category
          if (hasUnsetDates(task)) {
            const earliestDate = getEarliestDateInCategory(parentTask.id)
            updateData.start_date = formatDateForInput(earliestDate)
            updateData.end_date = formatDateForInput(earliestDate)

            // Update Gantt task
            task.start_date = earliestDate
            task.end_date = new Date(earliestDate)
            gantt.updateTask(id)
          }
        } else if (!newParentId && parentTask && parentTask.type === 'Project') {
          // Moving to project root, clear category
          updateData.task_category_id = null
        }

        updateSiteTaskGroup(siteTaskGroupId, updateData).then(() => {
          // Update the task's taskCategoryId in the UI to match what was saved
          if ('task_category_id' in updateData) {
            task.taskCategoryId = updateData.task_category_id
          }
          addNotification(
            '<span class="fa fa-cloud-arrow-up me-2"></span>' +
            i18next.t('command_result.saved'), 'bg-success', 'gantt-task-saved')
        })
      }
      // Store old parent for next time
      task.$old_parent = newParentGanttId
    })

    // Handle task updates after dragging on timeline
    gantt.attachEvent("onAfterTaskDrag", (id) => {
      const task = gantt.getTask(id)

      // If a SiteTaskGroup is dragged/resized, update all child tasks
      if (isSiteTaskGroup(task)) {
        this.updateChildTaskDatesFromSiteTaskGroup(task)

        // Save the SiteTaskGroup's new dates
        const siteTaskGroupId = extractId(id)
        updateSiteTaskGroup(siteTaskGroupId, {
          start_date: task.start_date,
          end_date: task.end_date
        }).then(() => {
          addNotification(
            '<span class="fa fa-cloud-arrow-up me-2"></span>' +
            i18next.t('command_result.saved'), 'bg-success', 'gantt-task-saved')
        })
      }
    })

    // Prevent dragging/resizing of tasks inside SiteTaskGroups;
    // also capture work-day count before a move drag so skip mode can restore it.
    gantt.attachEvent("onBeforeTaskDrag", (id, mode) => {
      const task = gantt.getTask(id)

      // SiteTaskGroups are always draggable (manual mode)
      if (isSiteTaskGroup(task)) {
        return true
      }

      // For SiteTasks, prevent drag if inside any SiteTaskGroup
      if (task.type === 'SiteTask' && shouldDisableTaskDrag(task)) {
        return false
      }

      // Skip mode: remember how many working days this task spans before the drag
      if (mode === 'move' && this.weekendMode === 'skip') {
        this._skipModePendingWorkDays = this._countWorkDays(task.start_date, task.end_date)
      }

      return true
    })
  }

  // Update all child SiteTask dates to match the SiteTaskGroup's dates
  updateChildTaskDatesFromSiteTaskGroup(siteTaskGroup) {
    const children = gantt.getChildren(siteTaskGroup.id)

    children.forEach(childId => {
      const child = gantt.getTask(childId)
      if (child.type === 'SiteTask') {
        child.start_date = new Date(siteTaskGroup.start_date)
        child.end_date = new Date(siteTaskGroup.end_date)
        gantt.updateTask(childId)

        // Save to backend
        const siteTaskId = extractId(childId)
        updateSiteTask(siteTaskId, {
          start_date: formatDateForInput(child.start_date),
          end_date: formatDateForInput(child.end_date)
        })
      }
    })
  }

  // Recursively update all children's progress to match the parent group
  // Backend already propagates this, so we just update the UI
  updateChildProgressFromSiteTaskGroup(siteTaskGroup) {
    const updateChildrenRecursively = (parentId, progress) => {
      const children = gantt.getChildren(parentId)

      children.forEach(childId => {
        const child = gantt.getTask(childId)
        if (child.type === 'SiteTask' || child.originalType === 'SiteTaskGroup') {
          child.progress = progress
          gantt.updateTask(childId)

          // If it's a group, also update its children
          if (child.originalType === 'SiteTaskGroup') {
            updateChildrenRecursively(childId, progress)
          }
        }
      })
    }

    updateChildrenRecursively(siteTaskGroup.id, siteTaskGroup.progress)
  }

  getRootProject(task) {
    if (!task) return null
    if (task.type === 'Project') return task

    let current = task
    while (current.parent && current.parent !== '0') {
      current = gantt.getTask(current.parent)
      if (current.type === 'Project') return current
    }

    return null
  }

  getCategoryForTask(task) {
    if (!task) return null

    // If task has originalCategoryId stored, use that
    if (task.originalCategoryId) return task.originalCategoryId

    // Otherwise, traverse up to find the category
    let current = task
    while (current.parent && current.parent !== '0') {
      const parent = gantt.getTask(current.parent)
      if (parent.type === 'Category') {
        return parent.id
      }
      current = parent
    }

    return null
  }

  getCategoryForParent(parent) {
    if (!parent) return null
    if (parent.type === 'Category') return parent.id

    // If parent is a SiteTaskGroup, check if it has taskCategoryId first
    if (parent.originalType === 'SiteTaskGroup') {
      // First check if the group itself has a category association
      if (parent.taskCategoryId) {
        return `${this.getRootProject(parent).id}:${parent.taskCategoryId}`
      }

      // Otherwise, traverse up to find category
      let current = parent
      while (current.parent && current.parent !== '0') {
        const parentTask = gantt.getTask(current.parent)
        if (parentTask.type === 'Category') {
          return parentTask.id
        }
        current = parentTask
      }
    }

    return null
  }

  isGroupAtRoot(group) {
    if (!group || group.originalType !== 'SiteTaskGroup') return false

    // If group has a taskCategoryId, it's associated with a category, not at root
    if (group.taskCategoryId) return false

    // A group is at root if its parent is a Project (not a Category)
    if (group.parent && group.parent !== '0') {
      try {
        const parent = gantt.getTask(group.parent)
        return parent.type === 'Project'
      } catch (e) {
        // Parent might not exist in gantt (e.g., during drag operation)
        return false
      }
    }

    return false
  }

  addTypeStyles() {
    const styleId = 'gantt-type-styles'
    let styleElement = document.getElementById(styleId)

    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = styleId
      document.head.appendChild(styleElement)
    }

    const typeStyles = Object.entries(this.typeColors).map(([type, color]) => `
      .gantt-type-${type} {
        background-color: ${color} !important;
      }
      .gantt_task_line.gantt-bar-type-${type} {
        background-color: ${type === 'SiteTaskGroup' ? this.typeColors['SiteTaskGroupBg'] : color};
        border: 1px solid ${darkenColor(color, 50)};
      }
      .gantt_task_line.gantt-bar-type-${type} .gantt_task_progress {
        background-color: ${darkenColor(color, 30)};
      }
    `).join('\n')

    styleElement.textContent = typeStyles
  }

  renderLegend() {
    if (!this.hasLegendTarget) return

    const legendItems = [
      { type: 'Project', color: this.typeColors['Project'], borderColor: '#4a9ad4', labelKey: 'project' },
      { type: 'Category', color: this.typeColors['Category'], borderColor: '#c9a076', labelKey: 'category' },
      { type: 'SiteTaskGroup', color: this.typeColors['SiteTaskGroupBg'], borderColor: '#8e8e8e', labelKey: 'site_task_group' },
      { type: 'SiteTask', color: this.typeColors['SiteTask'], borderColor: '#c8ce6b', labelKey: 'task' }
    ]

    this.legendTarget.innerHTML = legendItems.map(item => {
      const label = t(`legend.${item.labelKey}`)
      return `
        <div class="d-flex align-items-center gap-2">
          <span style="background-color: ${item.color}; width: 16px; height: 16px; flex-shrink: 0; display: inline-block; border: 1px solid ${item.borderColor}; border-radius: 2px;"></span>
          <small class="text-muted">${label}</small>
        </div>
      `
    }).join('')
  }

  async loadData() {
    const ganttData = await getGanttData(this.organizationIdValue, this.projectIdValue)
    gantt.parse(ganttData)

    // Initialize $old_parent for all tasks to track parent changes
    gantt.eachTask((task) => {
      task.$old_parent = task.parent === '0' ? null : task.parent
    })

    this.initializeCollapseState()
  }

  setupUpdateCallbacks() {
    gantt.attachEvent('onBeforeTaskUpdate', async (id, task) => {
      console.log('onBeforeTaskUpdate', JSON.stringify([task.start_date, task.end_date]))
      // Skip mode: extend end_date so the task spans the same number of working days
      // as it had before the drag (weekends are bridged over, not counted).
      if (this._skipModePendingWorkDays != null) {
        task.end_date = this._addWorkDays(task.start_date, this._skipModePendingWorkDays)
        this._skipModePendingWorkDays = null
      }
    })
    gantt.attachEvent('onAfterTaskUpdate', async (id, task) => {
      const extractedId = extractId(id)

      if (task.originalType === 'SiteTaskGroup') {
        const parentTask = task.parent ? gantt.getTask(task.parent) : null
        const actualParentId = (parentTask && parentTask.originalType === 'SiteTaskGroup') ? extractId(task.parent) : null

        // Determine task_category_id from parent if it's a category
        let taskCategoryId = task.taskCategoryId // Preserve existing
        if (parentTask && parentTask.type === 'Category') {
          const categoryIdParts = parentTask.id.split(':')
          taskCategoryId = categoryIdParts.length === 2 ? categoryIdParts[1] : null
        } else if (!actualParentId && parentTask && parentTask.type === 'Project') {
          // If parent is project root, clear category
          taskCategoryId = null
        }

        const rootProject = this.getRootProject(task)
        const projectId = rootProject ? rootProject.id : this.projectIdValue
        const siteTaskGroupData = {
          name: task.text,
          start_date: task.start_date,
          end_date: task.end_date,
          project_id: projectId,
          parent_id: actualParentId,
          task_category_id: taskCategoryId,
          ...(task.progress != null && { percentage_completed: Math.round(task.progress * 100) }),
        }

        await updateSiteTaskGroup(extractedId, siteTaskGroupData)

        // Update child task dates to match folder dates (same as when dragging)
        this.updateChildTaskDatesFromSiteTaskGroup(task)

        // Update child progress to match group progress
        if (task.progress != null) {
          this.updateChildProgressFromSiteTaskGroup(task)
        }

        addNotification(
          '<span class="fa fa-cloud-arrow-up me-2"></span>' +
          i18next.t('command_result.saved'), 'bg-success', 'gantt-task-saved')
      } else if (task.type === 'SiteTask') {
        const site_task = {
          start_date: task.start_date,
          end_date: task.end_date,
          ...(task.progress != null && { percentage_completed: task.progress * 100.0 }),
        }
        console.log(JSON.stringify([task.start_date, task.end_date]))

        updateSiteTask(extractedId, site_task).then((_siteTask) => {
          addNotification(
            '<span class="fa fa-cloud-arrow-up me-2"></span>' +
            i18next.t('command_result.saved'), 'bg-success', 'gantt-task-saved')
        })
      }
    })
  }

  setView(event) {
    const viewType = event.params.view
    this.currentView = viewType
    this._setActiveViewButton(viewType, event.target)
    this.updateGanttScales()
  }

  toggleHourDayScale(event) {
    this.isHourScale = event.target.checked
    this.currentScale = this.isHourScale ? 'hour' : 'day'
    this.updateGanttScales()
  }

  setWeekendMode(event) {
    this.weekendMode = event.target.value
    gantt.render()
  }

  // Count working days (Mon–Fri) in [start, end)
  _countWorkDays(start, end) {
    let count = 0
    const current = new Date(start)
    const endDate = new Date(end)
    while (current < endDate) {
      const day = current.getDay()
      if (day !== 0 && day !== 6) count++
      current.setDate(current.getDate() + 1)
    }
    return count
  }

  // Return the date that is `workDays` working days after `start`
  _addWorkDays(start, workDays) {
    const result = new Date(start)
    let added = 0
    while (added < workDays) {
      result.setDate(result.getDate() + 1)
      const day = result.getDay()
      if (day !== 0 && day !== 6) added++
    }
    return result
  }

  updateGanttScales() {
    switch (this.currentView) {
      case 'day':
        if (this.isHourScale) {
          gantt.config.scales = [
            { unit: 'day', step: 1, format: '%d %M, %Y' },
            { unit: 'hour', step: 1, format: '%H:00' },
          ]
          gantt.config.min_column_width = 60
        } else {
          gantt.config.scales = [
            { unit: 'day', step: 1, format: '%d %M, %Y' },
          ]
          gantt.config.min_column_width = 200
        }
        gantt.config.scale_height = this.isHourScale ? 50 : 25
        break

      case 'week':
        if (this.isHourScale) {
          gantt.config.scales = [
            { unit: 'week', step: 1, format: weekFormat },
            { unit: 'day', step: 1, format: '%D %d' },
            { unit: 'hour', step: 4, format: '%H' },
          ]
          gantt.config.scale_height = 75
          gantt.config.min_column_width = 25
        } else {
          gantt.config.scales = [
            { unit: 'week', step: 1, format: weekFormat },
            { unit: 'day', step: 1, format: '%D %d' },
          ]
          gantt.config.scale_height = 50
          gantt.config.min_column_width = 80
        }
        break

      case 'month':
        // Month view: Show 2-6 months
        if (this.isHourScale) {
          gantt.config.scales = [
            { unit: 'year', step: 1, format: '%Y' },
            { unit: 'month', step: 1, format: '%M' },
            { unit: 'day', step: 1, format: '%d' },
            { unit: 'hour', step: 12, format: '%H' },
          ]
          gantt.config.min_column_width = 8
        } else {
          gantt.config.scales = [
            { unit: 'year', step: 1, format: '%Y' },
            { unit: 'month', step: 1, format: '%M' },
            { unit: 'day', step: 1, format: '%d' },
          ]
          gantt.config.min_column_width = 30
        }
        gantt.config.scale_height = this.isHourScale ? 100 : 75
        break

      default:
        console.warn(`Unknown view type: ${this.currentView}`)
        return
    }
    gantt.render()
  }

  addSiteTaskGroup(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const parentId = button.dataset.id

    const parentTask = parentId ? gantt.getTask(parentId) : null
    const rootProject = parentTask ? this.getRootProject(parentTask) : null
    const projectId = rootProject ? rootProject.id : this.projectIdValue

    if (!projectId) {
      addNotification(
        '<span class="fa fa-exclamation-triangle me-2"></span>' +
        i18next.t('errors.missing_project_id_for_site_task_group', { ns: 'controllers__sites__dhtmlx_gantt_show_controller' }), 'bg-danger', 'gantt-error')
      return
    }

    // Open SiteTaskGroup dialog in create mode
    this.openSiteTaskGroupDialog({ parentId, isNew: true })
  }

  exportToPDF() {
    gantt.exportToPDF({
      format: 'A4',
      orientation: 'portrait',
      header: '<style>.cmp-gantt-row-actions { display: none !important; }</style>',
    })
  }

  _setActiveViewButton(viewType, target) {
    const buttons = this.element.querySelectorAll('[data-sites--dhtmlx-gantt-show-view-param]')
    buttons.forEach(btn => btn.classList.remove('active'))
    if (target) {
      target.classList.add('active')
    } else {
      const btn = this.element.querySelector(`[data-sites--dhtmlx-gantt-show-view-param="${viewType}"]`)
      if (btn) btn.classList.add('active')
    }
  }

  // Markers are PRO-only since dhtmlx-gantt 10
  addTodayMarker() {
    this.todayMarkerEventIds?.forEach(id => gantt.detachEvent(id))
    this.todayMarkerEventIds = ['onGanttRender', 'onDataRender'].map(eventName =>
      gantt.attachEvent(eventName, () => this.renderTodayMarker())
    )
    this.renderTodayMarker()
  }

  renderTodayMarker() {
    const container = gantt.$task_data
    if (!container) return

    let marker = container.querySelector('.cmp-gantt-today-marker')
    if (!marker) {
      marker = document.createElement('div')
      marker.className = 'cmp-gantt-today-marker'
      marker.title = i18next.t('views.gantt.today_marker_title')
      const label = document.createElement('div')
      label.className = 'cmp-gantt-today-marker-label'
      label.textContent = i18next.t('views.gantt.today')
      marker.appendChild(label)
      container.appendChild(marker)
    }

    const today = new Date()
    const { min_date: minDate, max_date: maxDate } = gantt.getState()
    const isVisible = minDate && maxDate && today >= minDate && today <= maxDate
    marker.classList.toggle('d-none', !isVisible)
    if (!isVisible) return

    marker.style.left = `${gantt.posFromDate(today)}px`
    marker.style.height = `${Math.max(container.scrollHeight, container.offsetHeight)}px`
  }

  showToday() {
    const today = new Date()
    gantt.showDate(today)
  }

  initializeCollapseState() {
    // Set default zoom level based on projectId presence
    if (this.projectIdValue) {
      this.expandAll()
    } else {
      this.collapseAll()
    }
  }

  collapseAll() {
    collapseAllUtils()
    setExpandCollapseActiveButton(this.element, 'collapseAll')
  }

  expandToCategories() {
    expandToCategoriesUtils()
    setExpandCollapseActiveButton(this.element, 'expandToCategories')
  }

  expandAll() {
    expandAllUtils()
    setExpandCollapseActiveButton(this.element, 'expandAll')
  }

  openSiteTaskGroupSettingsDialog(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const ganttId = button.dataset.id

    this.openSiteTaskGroupDialog({ ganttId, isNew: false })
  }

  openSiteTaskGroupDialog({ ganttId = null, parentId = null, isNew = false }) {
    // For edit mode, get existing task data; for create mode, use context-based defaults
    const task = isNew ? null : gantt.getTask(ganttId)
    const siteTaskGroupName = task?.text || ''

    let startDate, endDate

    if (isNew) {
      // New SiteTaskGroup: use context-based default dates
      const defaultDates = getDefaultDatesForSiteTaskGroup(parentId)
      startDate = defaultDates.startDate
      endDate = defaultDates.endDate
    } else {
      // Edit mode: use existing values
      startDate = task?.start_date
      endDate = task?.end_date
    }

    const modalTitle = isNew
      ? t('site_task_group.new')
      : t('site_task_group.settings')

    // Create modal HTML with Stimulus data attributes
    const modalHtml = `
      <div class="modal fade" id="siteTaskGroupSettingsModal" tabindex="-1" aria-labelledby="siteTaskGroupSettingsModalLabel" aria-hidden="true"
           data-sites--dhtmlx-gantt-show-target="siteTaskGroupModal"
           data-gantt-id="${ganttId || ''}"
           data-parent-id="${parentId || ''}"
           data-is-new="${isNew}">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="siteTaskGroupSettingsModalLabel">
                ${modalTitle}
              </h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <form id="siteTaskGroupSettingsForm" data-action="submit->sites--dhtmlx-gantt-show#saveSiteTaskGroup">
                <div class="mb-3">
                  <label for="siteTaskGroupName" class="form-label">${t('site_task_group.name')}</label>
                  <input type="text" class="form-control" id="siteTaskGroupName"
                         data-sites--dhtmlx-gantt-show-target="siteTaskGroupName"
                         value="${siteTaskGroupName}" required>
                </div>
                <!-- Date mode selector hidden for now - always use manual mode -->
                <div data-sites--dhtmlx-gantt-show-target="manualDatesSection">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label for="siteTaskGroupStartDate" class="form-label">${i18next.t('views.gantt.start_date')}</label>
                      <input type="date" class="form-control" id="siteTaskGroupStartDate"
                             data-sites--dhtmlx-gantt-show-target="siteTaskGroupStartDate"
                             value="${formatDateForInput(startDate)}">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label for="siteTaskGroupEndDate" class="form-label">${i18next.t('views.gantt.end_date')}</label>
                      <input type="date" class="form-control" id="siteTaskGroupEndDate"
                             data-sites--dhtmlx-gantt-show-target="siteTaskGroupEndDate"
                             value="${formatDateForInput(endDate)}">
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div class="modal-footer justify-content-between">
              ${isNew ? '<div></div>' : `
                <button title="${t('site_task_group.delete')}" type="button" class="btn btn-outline-danger"
                        data-action="click->sites--dhtmlx-gantt-show#deleteSiteTaskGroup">
                  <i class="fas fa-trash me-1"></i>
                </button>
              `}
              <div>
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${i18next.t('command.cancel')}</button>
                <button type="button" class="btn btn-primary"
                        data-action="click->sites--dhtmlx-gantt-show#saveSiteTaskGroup">
                  ${i18next.t('command.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    // Remove existing modal if any
    const existingModal = document.getElementById('siteTaskGroupSettingsModal')
    if (existingModal) {
      existingModal.remove()
    }

    // Add modal to DOM inside the controller's element
    this.element.insertAdjacentHTML('beforeend', modalHtml)

    const modalElement = this.siteTaskGroupModalTarget
    this.siteTaskGroupModalInstance = new Modal(modalElement)

    // Clean up modal on hide
    modalElement.addEventListener('hidden.bs.modal', () => {
      modalElement.remove()
      this.siteTaskGroupModalInstance = null
    })

    this.siteTaskGroupModalInstance.show()

    // Focus on name input after modal is shown
    modalElement.addEventListener('shown.bs.modal', () => {
      this.siteTaskGroupNameTarget.focus()
    })
  }

  async saveSiteTaskGroup(event) {
    if (event) event.preventDefault()
    const isNew = this.siteTaskGroupModalTarget.dataset.isNew === 'true'
    const ganttId = this.siteTaskGroupModalTarget.dataset.ganttId
    const parentId = this.siteTaskGroupModalTarget.dataset.parentId

    const name = this.siteTaskGroupNameTarget.value.trim()
    const startDate = this.siteTaskGroupStartDateTarget.value
    const endDate = this.siteTaskGroupEndDateTarget.value

    if (!name) {
      addNotification(
        '<span class="fa fa-exclamation-triangle me-2"></span>' +
        t('errors.name_required'), 'bg-danger', 'gantt-error')
      return
    }

    if (isNew) {
      await this.createSiteTaskGroup({ parentId, name, startDate, endDate })
    } else {
      await this.updateExistingSiteTaskGroup({ ganttId, name, startDate, endDate })
    }
  }

  async createSiteTaskGroup({ parentId, name, startDate, endDate }) {
    const parentTask = parentId ? gantt.getTask(parentId) : null
    const actualParentId = (parentTask && parentTask.originalType === 'SiteTaskGroup') ? extractId(parentId) : null

    // Determine task_category_id based on parent
    let taskCategoryId = null
    if (parentTask && parentTask.type === 'Category') {
      const categoryIdParts = parentTask.id.split(':')
      taskCategoryId = categoryIdParts.length === 2 ? categoryIdParts[1] : null
    }

    const rootProject = parentTask ? this.getRootProject(parentTask) : null
    const projectId = rootProject ? rootProject.id : this.projectIdValue

    const siteTaskGroupData = {
      name,
      date_mode: 'manual',
      project_id: projectId,
      parent_id: actualParentId,
      task_category_id: taskCategoryId,
    }

    if (startDate) siteTaskGroupData.start_date = startDate
    if (endDate) siteTaskGroupData.end_date = endDate

    try {
      const response = await createSiteTaskGroup(siteTaskGroupData)
      if (response.site_task_group) {
        const newGanttTask = {
          id: `siteTaskGroup-${response.site_task_group.id}`,
          type: gantt.config.types.project,
          originalType: 'SiteTaskGroup',
          text: response.site_task_group.name,
          open: true,
          start_date: new Date(response.site_task_group.start_date),
          end_date: new Date(response.site_task_group.end_date),
          duration: response.site_task_group.estimated_days || 0,
          progress: (response.site_task_group.percentage_completed || 0) / 100.0,
          taskCategoryId: response.site_task_group.task_category_id,
          dateMode: response.site_task_group.date_mode,
        }
        gantt.addTask(newGanttTask, parentId)
        // Initialize $old_parent for the new task
        const addedTask = gantt.getTask(newGanttTask.id)
        addedTask.$old_parent = parentId === '0' ? null : parentId
        addNotification(
          '<span class="fa fa-cloud-arrow-up me-2"></span>' +
          i18next.t('command_result.created'), 'bg-success', 'gantt-task-saved')
      }
      this.siteTaskGroupModalInstance.hide()
    } catch (error) {
      addNotification(
        '<span class="fa fa-exclamation-triangle me-2"></span>' +
        t('errors.save_failed'), 'bg-danger', 'gantt-error')
    }
  }

  async updateExistingSiteTaskGroup({ ganttId, name, startDate, endDate }) {
    const siteTaskGroupId = extractId(ganttId)
    const task = gantt.getTask(ganttId)

    const updateData = {
      name,
      date_mode: 'manual',
    }

    if (startDate) updateData.start_date = startDate
    if (endDate) updateData.end_date = endDate

    try {
      await updateSiteTaskGroup(siteTaskGroupId, updateData)

      // Update gantt task
      task.text = name
      task.dateMode = 'manual'
      if (startDate) task.start_date = new Date(startDate)
      if (endDate) task.end_date = new Date(endDate)
      gantt.updateTask(ganttId)
      this.updateChildTaskDatesFromSiteTaskGroup(task)

      addNotification(
        '<span class="fa fa-cloud-arrow-up me-2"></span>' +
        i18next.t('command_result.saved'), 'bg-success', 'gantt-task-saved')

      this.siteTaskGroupModalInstance.hide()
    } catch (error) {
      addNotification(
        '<span class="fa fa-exclamation-triangle me-2"></span>' +
        t('errors.save_failed'), 'bg-danger', 'gantt-error')
    }
  }

  async deleteSiteTaskGroup() {
    const ganttId = this.siteTaskGroupModalTarget.dataset.ganttId
    const siteTaskGroupId = extractId(ganttId)

    if (confirm(i18next.t('site_task_group.delete_confirm', { ns: 'controllers__sites__dhtmlx_gantt_show_controller' }))) {
      try {
        await deleteSiteTaskGroup(siteTaskGroupId)
        gantt.deleteTask(ganttId)
        addNotification(
          '<span class="fa fa-cloud-arrow-up me-2"></span>' +
          i18next.t('command_result.deleted'), 'bg-success', 'gantt-task-deleted')
        this.siteTaskGroupModalInstance.hide()
      } catch (error) {
        addNotification(
          '<span class="fa fa-exclamation-triangle me-2"></span>' +
          t('errors.delete_failed'), 'bg-danger', 'gantt-error')
      }
    }
  }
}